import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { buildFormSteps, getTotalQuestions } from '../lib/formData'
import { db } from '../lib/supabase'

const DRAFT_KEY_PREFIX = 'csi-draft-'

export function useSurveyForm(options = {}) {
  const { userId, clientPairId, initialValues, initialStep } = options
  const [currentStep, setCurrentStep] = useState(initialStep || 0)
  const [formValues, setFormValues] = useState(initialValues || {})
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [saveStatus, setSaveStatus] = useState(null) // null | 'saving' | 'saved' | 'error'
  const saveTimeoutRef = useRef(null)

  const steps = useMemo(() => buildFormSteps(), [])
  const totalQuestions = useMemo(() => getTotalQuestions(), [])

  const answeredQuestions = useMemo(() => {
    return Object.keys(formValues).filter(k => {
      const val = formValues[k]
      return val !== undefined && val !== '' && (Array.isArray(val) ? val.length > 0 : true)
    }).length
  }, [formValues])

  const progressPercent = useMemo(() => {
    return Math.round((answeredQuestions / totalQuestions) * 100)
  }, [answeredQuestions, totalQuestions])

  const currentStepData = steps[currentStep]
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === steps.length - 1

  // localStorage auto-save: write on every change (only for authenticated users)
  useEffect(() => {
    if (!userId) return
    try {
      localStorage.setItem(DRAFT_KEY_PREFIX + userId, JSON.stringify({
        formValues,
        currentStep,
        ts: Date.now(),
      }))
    } catch {
      // localStorage full or unavailable — silent fail
    }
  }, [formValues, currentStep, userId])

  // Cleanup save status timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  // Server-side draft save (non-blocking, called on step transition)
  const saveDraftToServer = useCallback(async (step) => {
    if (!userId) return

    setSaveStatus('saving')
    try {
      await db.saveDraft(userId, clientPairId, formValues, step)
      setSaveStatus('saved')
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      console.error('Draft save failed:', err)
      setSaveStatus('error')
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => setSaveStatus(null), 5000)
    }
  }, [userId, clientPairId, formValues])

  const handleChange = useCallback((id, value) => {
    setFormValues(prev => ({ ...prev, [id]: value }))
    // Clear error when user answers
    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[id]
        return newErrors
      })
    }
  }, [errors])

  const validateStep = useCallback(() => {
    const step = steps[currentStep]
    const newErrors = {}

    if (step.type === 'instructions') {
      return true // No validation for instructions
    }

    if (step.type === 'profile') {
      step.data.forEach(q => {
        if (q.required) {
          const val = formValues[q.id]
          if (!val || (Array.isArray(val) && val.length === 0)) {
            newErrors[q.id] = 'This field is required'
          } else if (q.inputType === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
            newErrors[q.id] = 'Please enter a valid email address'
          } else if (q.inputType === 'number') {
            const num = parseInt(val)
            if (isNaN(num)) {
              newErrors[q.id] = 'Please enter a valid number'
            } else if (q.min && num < q.min) {
              newErrors[q.id] = `Must be at least ${q.min}`
            } else if (q.max && num > q.max) {
              newErrors[q.id] = `Must be at most ${q.max}`
            }
          }
        }
      })
    }

    if (step.type === 'matrix') {
      step.data.questions.forEach(q => {
        if (step.data.required && !formValues[q.id]) {
          newErrors[q.id] = 'Please select an option'
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [currentStep, steps, formValues])

  const goToNext = useCallback(() => {
    if (!validateStep()) {
      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey) {
        const element = document.querySelector(`[data-question="${firstErrorKey}"]`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
      return false
    }

    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1
      setCurrentStep(nextStep)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      // Non-blocking server save after advancing
      saveDraftToServer(nextStep)
      return true
    }
    return false
  }, [currentStep, steps.length, validateStep, errors, saveDraftToServer])

  const goToPrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return true
    }
    return false
  }, [currentStep])

  const goToStep = useCallback((step) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [steps.length])

  const submitForm = useCallback(async () => {
    if (!validateStep()) return false

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Check for existing submission (skip for authenticated clients with userId)
      if (!userId) {
        const existing = await db.checkExistingSubmission(formValues.q2)
        if (existing?.completed_at) {
          setSubmitError('This email has already submitted a response.')
          setIsSubmitting(false)
          return false
        }
      }

      // Submit to Supabase
      await db.submitSurvey(formValues, { userId, clientPairId })

      // Clean up drafts after successful submission
      if (userId) {
        try {
          await db.deleteDraft(userId)
        } catch (e) {
          console.log('Draft cleanup skipped:', e.message)
        }
        try {
          localStorage.removeItem(DRAFT_KEY_PREFIX + userId)
        } catch {
          // silent
        }
      }

      // Try to link partners if both have submitted (skip for authenticated — trigger handles it)
      if (!userId) {
        try {
          await db.linkPartners(formValues.q2, formValues.q6)
        } catch (e) {
          // Partner linking is optional, don't fail the submission
          console.log('Partner linking skipped:', e.message)
        }
      }

      setIsSubmitted(true)
      return true
    } catch (error) {
      console.error('Submission error:', error)
      setSubmitError(error.message || 'An error occurred while submitting. Please try again.')
      return false
    } finally {
      setIsSubmitting(false)
    }
  }, [formValues, validateStep, userId, clientPairId])

  const resetForm = useCallback(() => {
    setFormValues({})
    setErrors({})
    setCurrentStep(0)
    setIsSubmitted(false)
    setSubmitError(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return {
    // State
    currentStep,
    totalSteps: steps.length,
    currentStepData,
    formValues,
    errors,
    isSubmitting,
    isSubmitted,
    submitError,
    progressPercent,
    answeredQuestions,
    totalQuestions,
    isFirstStep,
    isLastStep,
    saveStatus,

    // Actions
    handleChange,
    goToNext,
    goToPrev,
    goToStep,
    submitForm,
    resetForm,
    validateStep,
  }
}

// Helper to load localStorage draft (used by AuthenticatedSurvey)
export function getLocalDraft(userId) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY_PREFIX + userId)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Helper to clear localStorage draft
export function clearLocalDraft(userId) {
  try {
    localStorage.removeItem(DRAFT_KEY_PREFIX + userId)
  } catch {
    // silent
  }
}
